import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import cmsApi, { cmsTokenStore } from '@/lib/cmsApi';
import { logger } from '@/lib/logger';

const CmsAuthContext = createContext(null);

export const useCmsAuth = () => useContext(CmsAuthContext);

export const CmsAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    try {
      const { data } = await cmsApi.get('/auth/me');
      setUser(data);
    } catch (err) {
      logger.warn('CMS session check failed:', err?.response?.status || err.message);
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password, totpCode) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    const res = await cmsApi.post('/auth/login', payload);
    if (res.data.requires_2fa) return res.data;
    if (res.data.token) cmsTokenStore.set(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await cmsApi.post('/auth/logout'); } catch (err) { logger.warn('CMS logout request failed:', err.message); }
    cmsTokenStore.clear();
    setUser(null);
    // Caller is expected to navigate via React Router (smooth client-side route change).
  };

  const value = useMemo(() => ({ user, loading, login, logout }), [user, loading]);

  return (
    <CmsAuthContext.Provider value={value}>
      {children}
    </CmsAuthContext.Provider>
  );
};
